import {
	EXTERNAL_ID,
	GENERIC_IDP_ID,
	GROUP,
	IdPCommonInputFieldName,
	IdPOidcInputFieldName,
	IdPSamlInputFieldName,
	IdPXaaInputFieldName,
	MAPPING_FIELD_NAMES,
	Method,
	SCIMMethod,
	SPOidcDataFieldName,
	SPSamlDataFieldName,
	SPScimDataFieldName,
	SPXaaDataFieldName,
	XaaJit
} from 'consts';
import { pick } from 'lodash';
import {
	IConfigMapping,
	IMethodConfigFields,
	IResConfigs,
	IUserAttributeMapping
} from 'types/api';
import { SPFields } from 'types/spFields';
import { getSSODomains } from './configUtils';
import { filterObjectByValues } from './objectUtils';

const queryMappingsToUserAttributesMappings = (
	configMappingData: IConfigMapping
): [IUserAttributeMapping[], IUserAttributeMapping | null] => {
	const { customAttributes, ...defaultAttributesMappings } =
		configMappingData || {};

	// note: group mapping in query object is {<descopeAttr> : <idpAttr>}
	const userAttributesMappings = Object.entries(defaultAttributesMappings)
		.filter(([spAttr]) => spAttr !== GROUP)
		.map(([spAttr, idpAttr]) => ({
			idpAttr,
			spAttr
		}));

	if (customAttributes) {
		userAttributesMappings.push(
			...Object.entries(customAttributes).map(([spAttr, idpAttr]) => ({
				idpAttr,
				spAttr
			}))
		);
	}

	const groupsEntry = Object.entries(defaultAttributesMappings).find(
		([spAttr]) => spAttr === GROUP
	);

	const groupsAttribute = groupsEntry
		? {
				idpAttr: groupsEntry[1],
				spAttr: GROUP
			}
		: null;

	return [userAttributesMappings, groupsAttribute];
};

const isEnabledConfig = (configsData: IResConfigs, method: Method): boolean => {
	if (method === Method.Saml) {
		return !!configsData.saml.enabled;
	}
	if (method === Method.Oidc) {
		return Boolean(
			configsData.oauth.providerSettings[configsData.tenant.id]?.enabled
		);
	}
	if (method === Method.Xaa) {
		return !!configsData.xaa?.enabled;
	}
	/* istanbul ignore next  */
	return false;
};

const querySamlToSamlFields = (
	configsData: IResConfigs
): [string, Method, IMethodConfigFields, number] => {
	const {
		saml: {
			idpMetadataURL,
			entityID,
			idpCert,
			idpURL,
			samlMappings,
			samlGroupsMappings,
			version,
			providerID,
			defaultSSORoles,
			rebacGroupsMappings
		}
	} = configsData;
	const idpId = providerID || GENERIC_IDP_ID;

	const domains = getSSODomains(configsData);

	const groupsAttributeMappings = !samlGroupsMappings
		? null
		: samlGroupsMappings.flatMap(({ groups, role }) =>
				groups.map((group) => ({ group, roleId: role.id }))
			);

	const [userAttributesMappings, groupsAttributeName] =
		queryMappingsToUserAttributesMappings(samlMappings);

	return [
		idpId,
		Method.Saml,
		filterObjectByValues(
			{
				[IdPSamlInputFieldName.IdPMetadataUrl]: idpMetadataURL,
				[IdPSamlInputFieldName.IdPIssuer]: entityID,
				[IdPSamlInputFieldName.IdPCertificate]: idpCert,
				[IdPSamlInputFieldName.IdPSsoUrl]: idpURL,
				[IdPCommonInputFieldName.SSODomains]: domains,
				[IdPCommonInputFieldName.UserAttributes]: userAttributesMappings,
				[IdPCommonInputFieldName.GroupsAttributeMapping]:
					groupsAttributeMappings,
				[IdPCommonInputFieldName.GroupsAttribute]: groupsAttributeName,
				[IdPCommonInputFieldName.DefaultSSORoles]: defaultSSORoles,
				[IdPCommonInputFieldName.RebacGroupsMappings]: rebacGroupsMappings
			},
			(v) => v !== undefined
		),
		version
	];
};

const queryOidcToOidcFields = (
	configsData: IResConfigs
): [string, Method, IMethodConfigFields, number] => {
	const tenantId = configsData.tenant.id;
	const {
		oauth: { version }
	} = configsData;

	const domains = getSSODomains(configsData);

	const {
		name,
		clientID,
		clientSecret,
		scope,
		grantType,
		issuer,
		tokenURL,
		authURL,
		userDataURL,
		JWKsURI,
		prompt,
		usePkce,
		userDataClaimsMapping,
		providerID,
		oidcGroupsMappings,
		defaultSSORoles,
		rebacGroupsMappings
	} = configsData.oauth.providerSettings[tenantId];
	const idpId = providerID || GENERIC_IDP_ID;

	const [userAttributesMappings, groupsAttributeName] =
		queryMappingsToUserAttributesMappings(userDataClaimsMapping);

	const groupsAttributeMappings = !oidcGroupsMappings
		? null
		: oidcGroupsMappings.flatMap(({ groups, role }) =>
				groups.map((group) => ({ group, roleId: role.id }))
			);

	return [
		idpId,
		Method.Oidc,
		filterObjectByValues(
			{
				[IdPOidcInputFieldName.ProviderName]: name,
				[IdPOidcInputFieldName.ClientId]: clientID,
				[IdPOidcInputFieldName.ClientSecret]: clientSecret,
				[IdPOidcInputFieldName.Scopes]: scope,
				[IdPOidcInputFieldName.GrantType]: grantType,
				[IdPOidcInputFieldName.Issuer]: issuer,
				[IdPOidcInputFieldName.AuthorizationEndpoint]: authURL,
				[IdPOidcInputFieldName.TokenEndpoint]: tokenURL,
				[IdPOidcInputFieldName.UserInfoEndpoint]: userDataURL,
				[IdPOidcInputFieldName.JWKsEndpoint]: JWKsURI,
				[IdPOidcInputFieldName.Prompt]: prompt || null,
				[IdPOidcInputFieldName.UsePkce]: usePkce,
				[IdPCommonInputFieldName.SSODomains]: domains,
				[IdPCommonInputFieldName.UserAttributes]: userAttributesMappings,
				[IdPCommonInputFieldName.GroupsAttributeMapping]:
					groupsAttributeMappings,
				[IdPCommonInputFieldName.GroupsAttribute]: groupsAttributeName,
				[IdPCommonInputFieldName.DefaultSSORoles]: defaultSSORoles,
				[IdPCommonInputFieldName.RebacGroupsMappings]: rebacGroupsMappings
			},
			(v) => v !== undefined
		),
		version
	];
};

// The persisted XAA provider id, resolved like OIDC's providerID || GENERIC_IDP_ID.
const queryToXaaProviderID = (configsData: IResConfigs): string =>
	configsData.xaa?.providerID || GENERIC_IDP_ID;

const queryXaaToXaaFields = (
	configsData: IResConfigs
): [string, Method, IMethodConfigFields, null] => {
	// Trust (issuer URL, jwks, signing, user-info, jit, attribute mapping) lives on the
	// single active issuer: the first/only entry in xaa.jwtBearerSettings.issuers, keyed by
	// its real issuer URL.
	const issuers = configsData.xaa?.jwtBearerSettings?.issuers ?? {};
	const [issuerKey] = Object.keys(issuers);
	const [firstIssuer] = Object.values(issuers);

	const {
		attributeMapping,
		jwksUri,
		signAlgorithm,
		userInfoUri,
		jitDisabled,
		externalIdFieldName
	} = firstIssuer ?? {};

	// Config-level mapping (group->role, default roles, FGA) lives on the top-level xaa
	// object, mirroring SAML/OIDC - the backend persists it only there.
	const { samlGroupsMappings, defaultSSORoles, rebacGroupsMappings } =
		configsData.xaa ?? {};

	const groupsAttributeMappings = !samlGroupsMappings
		? null
		: samlGroupsMappings.flatMap(({ groups, role }) =>
				(groups ?? []).map((group) => ({
					group,
					roleId: role?.id ?? null
				}))
			);

	const [userAttributesMappings, groupsAttributeName] =
		queryMappingsToUserAttributesMappings(attributeMapping ?? {});

	// The subject claim is persisted on the issuer's externalIdFieldName (the backend has
	// no per-issuer attributeMapping for externalID), so read it back from there and
	// surface it as the mandatory Login ID (externalID) row - otherwise a saved value is
	// dropped on load and the row re-seeds blank.
	if (
		externalIdFieldName &&
		!userAttributesMappings.some((mapping) => mapping.spAttr === EXTERNAL_ID)
	) {
		userAttributesMappings.push({
			idpAttr: externalIdFieldName,
			spAttr: EXTERNAL_ID
		});
	}

	// JIT provisioning is on unless the issuer explicitly disables it. Surfaced as the
	// content-placeable xaa-jit field (and also editable in the shared JIT section).
	let jit: XaaJit | undefined;
	if (firstIssuer) {
		jit = jitDisabled ? XaaJit.Disabled : XaaJit.Enabled;
	}

	return [
		queryToXaaProviderID(configsData),
		Method.Xaa,
		filterObjectByValues(
			{
				[IdPXaaInputFieldName.Issuer]: issuerKey,
				[IdPXaaInputFieldName.JwksUri]: jwksUri,
				[IdPXaaInputFieldName.SignAlgorithm]: signAlgorithm,
				[IdPXaaInputFieldName.UserInfoUri]: userInfoUri,
				[IdPXaaInputFieldName.Jit]: jit,
				[IdPCommonInputFieldName.UserAttributes]: userAttributesMappings,
				[IdPCommonInputFieldName.GroupsAttributeMapping]:
					groupsAttributeMappings,
				[IdPCommonInputFieldName.GroupsAttribute]: groupsAttributeName,
				[IdPCommonInputFieldName.DefaultSSORoles]: defaultSSORoles,
				[IdPCommonInputFieldName.RebacGroupsMappings]: rebacGroupsMappings
			},
			(v) => v !== undefined
		),
		null
	];
};

/**
 *
 * @param configsData
 * @returns [idpId, method, methodConfigFields, versionNumber]
 */
const queryToFieldsConfigs = (
	configsData: IResConfigs
): [
	string | null,
	Method | null,
	IMethodConfigFields | null,
	number | null
] => {
	if (isEnabledConfig(configsData, Method.Saml)) {
		return querySamlToSamlFields(configsData);
	}

	if (isEnabledConfig(configsData, Method.Oidc)) {
		return queryOidcToOidcFields(configsData);
	}

	// XAA is a standalone config (like SCIM) - it is NOT resolved here so it never
	// surfaces as the SSO idp/method. Its own resolvers are queryXaaToXaaFields (fields)
	// and queryToXaaIdPId (selection).
	return [null, null, {}, null];
};

/**
 * Mirrors the backend's enabled-first resolution of which method's
 * configuration drives SCIM mapping for generic SCIM setups: an enabled
 * method wins; with neither enabled, SAML connection fields win over a
 * configured OIDC provider.
 */
const queryToScimEffectiveMethod = (configsData: IResConfigs): Method => {
	if (isEnabledConfig(configsData, Method.Saml)) {
		return Method.Saml;
	}
	if (isEnabledConfig(configsData, Method.Oidc)) {
		return Method.Oidc;
	}

	const { saml } = configsData;
	const samlConfigured = !!(
		saml.idpURL ||
		saml.idpMetadataURL ||
		saml.idpCert ||
		saml.entityID
	);
	const oidcConfigured =
		!!configsData.oauth.providerSettings[configsData.tenant.id]?.clientID;

	if (!samlConfigured && oidcConfigured) {
		return Method.Oidc;
	}
	return Method.Saml;
};

/**
 * SCIM guides edit the mapping fields stored on the method's SSO
 * configuration, which drives SCIM provisioning even when that
 * configuration is disabled.
 * @returns [idpId, method, mappingConfigFields, versionNumber]
 */
const queryToScimMappingFieldsConfigs = (
	configsData: IResConfigs,
	method: Method
): [
	string | null,
	Method | null,
	IMethodConfigFields | null,
	number | null
] => {
	if (
		method === Method.Oidc &&
		!configsData.oauth.providerSettings[configsData.tenant.id]
	) {
		return [null, method, {}, null];
	}

	const [idpId, , methodConfigFields, version] =
		method === Method.Saml
			? querySamlToSamlFields(configsData)
			: queryOidcToOidcFields(configsData);

	return [
		idpId,
		method,
		pick(methodConfigFields, MAPPING_FIELD_NAMES),
		version
	];
};

const queryToScimIdPId = (
	configsData: IResConfigs
): [string | null, SCIMMethod | null] => {
	const oidcScimProviderID =
		configsData.oauth.providerSettings[configsData.tenant.id]?.scimProviderID;
	if (oidcScimProviderID) {
		return [oidcScimProviderID, SCIMMethod.Oidc];
	}
	if (configsData.saml.scimProviderID) {
		const scimIdPId = configsData.saml.scimProviderID;
		return [scimIdPId, SCIMMethod.Saml];
	}

	return [null, null];
};

// XAA (Cross-App Access / ID-JAG) selection resolver - mirrors queryToScimIdPId and is
// independent of SAML/OIDC so an XAA-configured tenant seeds its OWN selection slot, not
// the SSO idp/method. Returns the fixed XAA idp + method when a trusted issuer exists.
const queryToXaaIdPId = (
	configsData: IResConfigs
): [string | null, Method | null] => {
	const issuers = configsData.xaa?.jwtBearerSettings?.issuers ?? {};
	if (configsData.xaa?.enabled && Object.keys(issuers).length > 0) {
		// providerID remembers the selected IdP template card across reload, falling back
		// to the generic content idp (mirrors SAML/OIDC providerID || GENERIC_IDP_ID).
		return [queryToXaaProviderID(configsData), Method.Xaa];
	}
	return [null, null];
};

// The audience the requesting app must present. It has no tenant in it, so the IdP sends the
// tenant in the aud_tenant claim. The SP-URL fallback is only for backends that don't send it.
const deriveXaaAudience = (configsData: IResConfigs): string => {
	if (configsData.xaaAudience) {
		return configsData.xaaAudience;
	}
	const spHostUrl =
		configsData.oauth?.callbackUrl ||
		configsData.saml?.acsUrl ||
		configsData.oauth?.scimUrl ||
		configsData.saml?.scimUrl;
	if (!spHostUrl) {
		/* istanbul ignore next: backend always returns at least one SP URL */
		return '';
	}
	let origin: string;
	try {
		origin = new URL(spHostUrl).origin;
	} catch {
		/* istanbul ignore next: SP URLs are always absolute */
		return '';
	}
	return `${origin}/v1/apps/${configsData.tenant.projectID}`;
};

const queryToSpFields = (
	configsData: IResConfigs,
	method: Method
): SPFields => {
	if (method === Method.Saml) {
		return {
			[SPSamlDataFieldName.SpAcsUrl]: configsData.saml.acsUrl,
			[SPSamlDataFieldName.SpEntityID]: configsData.saml.descopeEntityID,
			[SPSamlDataFieldName.SpMetadataUrl]: configsData.saml.xmlMetadataUrl,
			[SPScimDataFieldName.SCIMURL]: configsData.saml.scimUrl
		};
	}

	if (method === Method.Xaa) {
		return {
			[SPXaaDataFieldName.Audience]: deriveXaaAudience(configsData),
			// The audience is project-level, so the tenant id is a field of its own: the IdP has to send
			// it in the assertion's aud_tenant claim for the exchange to resolve to this tenant.
			[SPXaaDataFieldName.TenantId]: configsData.tenant.id
		};
	}

	return {
		[SPOidcDataFieldName.CallbackUrl]: configsData.oauth.callbackUrl,
		[SPScimDataFieldName.SCIMURL]: configsData.oauth.scimUrl
	};
};

export {
	isEnabledConfig,
	queryOidcToOidcFields,
	querySamlToSamlFields,
	queryXaaToXaaFields,
	queryToFieldsConfigs,
	queryToScimEffectiveMethod,
	queryToScimIdPId,
	queryToScimMappingFieldsConfigs,
	queryToXaaIdPId,
	queryToSpFields
};
